import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Wallet, ExternalLink } from 'lucide-react';

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/pas-treasury-check/admin/stablecoin/positions`;
const n = (v, d = 2) => Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: d });

const Position = ({ p, onWithdraw, busy }) => {
  const [pct, setPct] = useState('100');
  return (
    <div className="bg-slate-900/70 border border-slate-700 rounded-lg p-3 space-y-2" data-testid={`position-${p.token_id}`}>
      <div className="flex justify-between items-center">
        <p className="text-sm text-white font-semibold capitalize">{p.kind} #{p.token_id} <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded ${p.in_range ? 'bg-emerald-500/20 text-emerald-300' : 'bg-slate-700 text-slate-400'}`}>{p.in_range ? 'trading now' : 'waiting for price'}</span></p>
        <a href={p.url} target="_blank" rel="noreferrer" className="text-cyan-300 text-xs inline-flex items-center gap-1">Uniswap <ExternalLink className="w-3 h-3" /></a>
      </div>
      <p className="text-xs text-slate-300 font-mono" data-testid={`position-amounts-${p.token_id}`}>{n(p.beth)} BETH USD unsold · <b className="text-emerald-300">{n(p.usdc, 6)} USDC</b> {p.kind === 'sell wall' ? 'from buyers' : 'in position'} · fees {n(p.uncollected_usdc, 6)} USDC</p>
      <div className="flex gap-2 items-center">
        <select value={pct} onChange={(e) => setPct(e.target.value)} className="px-2 py-1.5 bg-slate-950 border border-slate-600 rounded text-white text-xs" data-testid={`position-pct-${p.token_id}`}>
          {['100', '50', '25', '10', '0'].map(v => <option key={v} value={v}>{v === '0' ? 'Fees only' : `${v}%`}</option>)}
        </select>
        <button disabled={busy} onClick={() => onWithdraw(p, Number(pct))} className="px-3 py-1.5 bg-amber-600 hover:bg-amber-500 disabled:opacity-40 text-white rounded text-xs font-semibold" data-testid={`position-withdraw-${p.token_id}`}>{busy ? 'Working…' : 'Withdraw / collect'}</button>
      </div>
    </div>
  );
};

export const PoolPositionsPanel = ({ token, onDone }) => {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const load = useCallback(() => axios.get(BASE, { headers: { Authorization: `Bearer ${token}` } }).then(r => setRows(r.data.positions)).catch(() => setRows([])), [token]);
  useEffect(() => { load(); }, [load]);
  const withdraw = async (p, pct) => {
    const what = pct === 0 ? 'collect trading fees from' : `withdraw ${pct}% of`;
    if (!window.confirm(`${what} position #${p.token_id} into the treasury wallet? Withdrawn BETH USD stops being for sale.`)) return;
    setBusy(true);
    try { const r = await axios.post(`${BASE}/${p.token_id}/withdraw`, { percent: pct }, h); toast.success(r.data.message); load(); onDone && onDone(); }
    catch (e) { toast.error(e.response?.data?.detail?.toString() || 'Withdraw failed'); }
    setBusy(false);
  };
  return (
    <div className="border-t border-slate-700 pt-3 space-y-2" data-testid="pool-positions-panel">
      <p className="text-sm text-white font-semibold flex items-center gap-2"><Wallet className="w-4 h-4 text-emerald-400" /> Collect sell wall / pool positions</p>
      {rows === null && <p className="text-xs text-slate-500">Reading positions…</p>}
      {rows && !rows.length && <p className="text-xs text-slate-500">No treasury positions in the BETH USD/USDC pool.</p>}
      {rows && rows.map(p => <Position key={p.token_id} p={p} onWithdraw={withdraw} busy={busy} />)}
      <p className="text-[10px] text-slate-500">Withdrawing returns that share of the unsold BETH USD and the buyers' USDC to the treasury wallet. From there, send USDC to an exchange or off-ramp to cash out to your bank.</p>
    </div>
  );
};
